import { isSpinnerCovered } from '../game/Chain.js';
import {
  isDouble,
  tileEquals,
  type Chain,
  type Pip,
  type Tile,
  type Turn,
} from '../game/types.js';
import { MAX_SEGMENT_LENGTH, TILE_LONG, TILE_SHORT } from '../utils/constants.js';
import { rectBounds, type TileRect } from './TileRenderer.js';

/**
 * Geometric layout of the chain. Pure function: takes a Chain plus an
 * anchor (where the lead/spinner sits in canvas coords) and returns the
 * absolute placement of every tile in the chain plus each arm's outward
 * endpoint (used by input for click-targets on the open ends).
 *
 * The v1 layout is a flat cross — no edge-turning. Arms 0/1 extend along
 * the main axis (horizontal); arms 2/3 (perpendicular, when the spinner
 * exists) extend along the perpendicular axis. Edge-turning lives in a
 * follow-up — the engine doesn't depend on it.
 */

export interface Direction {
  readonly dx: -1 | 0 | 1;
  readonly dy: -1 | 0 | 1;
}

const E: Direction = { dx: 1, dy: 0 };
const W: Direction = { dx: -1, dy: 0 };
const N: Direction = { dx: 0, dy: -1 };
const S: Direction = { dx: 0, dy: 1 };

export interface PlacedTile {
  readonly rect: TileRect;
  readonly halfA: Pip;
  readonly halfB: Pip;
  readonly tile: Tile;
  /** Which arm placed this tile. -1 for the shared lead tile. */
  readonly armIndex: number;
  /**
   * The arm direction this tile was placed in. `null` for the lead (which is
   * placed without a direction). Doubles always render perpendicular to this
   * direction (long axis crosswise to chain travel). Consumers can read
   * `.dx`/`.dy` to derive the placement axis.
   */
  readonly direction: Direction | null;
}

export interface ArmEndpoint {
  readonly armIndex: number;
  /** Center of the open-end "click target" zone, just outside the last tile. */
  readonly cx: number;
  readonly cy: number;
  readonly direction: Direction;
  /** True if no tile has been played on this arm yet (latent perpendicular). */
  readonly empty: boolean;
  /**
   * Half the perpendicular extent of the outermost tile on this arm (relative
   * to `direction`). A fold (left/right turn) advances by this much along the
   * NEW direction before placing the next tile, so the folded tile anchors to
   * a corner of the previous tile rather than straddling its midpoint.
   */
  readonly lastPerpHalf: number;
  /**
   * Half the along-direction extent of the outermost tile on this arm
   * (relative to `direction`). Used with `lastPerpHalf` to back the cursor
   * up along `-direction` on a fold by `lastAlongHalf - newTilePerpHalf`, so
   * the folded non-double tile centers on the parent's near half instead of
   * straddling its outside corner.
   */
  readonly lastAlongHalf: number;
  /**
   * True when the outermost tile on this arm is a double (rendered
   * perpendicular to the arm direction). The next play must continue
   * straight off its short edge — left/right folds would attach to the
   * double's long side, which is only legal for the spinner (and that
   * connection is modeled by perpendicular arms 2/3, not by folding 0/1).
   * For empty arms this carries the lead-anchor's double-ness; the
   * `empty` flag itself already blocks folds in that state.
   */
  readonly anchorIsDouble: boolean;
}

export interface ChainLayout {
  readonly tiles: readonly PlacedTile[];
  readonly armEnds: readonly ArmEndpoint[];
  /** Bounding box of the laid-out chain, before applying any pan. */
  readonly bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export const EMPTY_CHAIN_LAYOUT: ChainLayout = {
  tiles: [],
  armEnds: [],
  bounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
};

/**
 * Per-arm parallel array of turns. `turnsByArm[armIdx][tileIdx]` is the
 * fold the player (or AI) chose when placing `chain.arms[armIdx].tiles[tileIdx]`.
 * Missing entries default to `'auto'`.
 */
export type TurnsByArm = readonly (readonly Turn[])[];

export function layoutChain(
  chain: Chain,
  centerX: number,
  centerY: number,
  turnsByArm: TurnsByArm = [],
): ChainLayout {
  if (chain.lead === null) return EMPTY_CHAIN_LAYOUT;

  const placed: PlacedTile[] = [];
  const armEnds: ArmEndpoint[] = [];

  const leadIsDouble = isDouble(chain.lead);
  const leadLong = TILE_LONG;
  const leadShort = TILE_SHORT;
  // Non-double lead: horizontal (long axis along main axis).
  // Double lead (spinner-lead): vertical (lateral — long axis perpendicular).
  const leadOrientation: TileRect['orientation'] = leadIsDouble
    ? 'vertical'
    : 'horizontal';
  const leadWidth = leadOrientation === 'horizontal' ? leadLong : leadShort;
  const leadHeight = leadOrientation === 'horizontal' ? leadShort : leadLong;
  const leadRect: TileRect = {
    x: centerX - leadWidth / 2,
    y: centerY - leadHeight / 2,
    long: leadLong,
    short: leadShort,
    orientation: leadOrientation,
  };

  placed.push({
    rect: leadRect,
    halfA: chain.lead[0],
    halfB: chain.lead[1],
    tile: chain.lead,
    armIndex: -1,
    direction: null,
  });

  // Spinner mid-chain — recorded the first time we encounter it on arm 0 or 1.
  // Seeded here only when the LEAD is the spinner: a double lead always
  // renders laterally, but it is not automatically the branch point (the
  // spinner is the first double *covered*), and seeding on shape alone would
  // hang the perpendicular arms off the wrong tile.
  const leadIsSpinner = chain.spinner !== null && tileEquals(chain.lead, chain.spinner);
  let spinnerCenter: { x: number; y: number } | null = leadIsSpinner
    ? { x: centerX, y: centerY }
    : null;
  let spinnerMainDirection: Direction | null = leadIsSpinner ? E : null;

  // Main arms (0, 1). Initial perp/along halves describe the lead's footprint
  // relative to the main axis: double lead (lateral) → perp=TILE_LONG/2,
  // along=TILE_SHORT/2; non-double lead (in-line) → perp=TILE_SHORT/2,
  // along=TILE_LONG/2.
  const mainInitialPerpHalf = leadIsDouble ? TILE_LONG / 2 : TILE_SHORT / 2;
  const mainInitialAlongHalf = leadIsDouble ? TILE_SHORT / 2 : TILE_LONG / 2;
  for (const armIndex of [0, 1]) {
    if (armIndex >= chain.arms.length) continue;
    const arm = chain.arms[armIndex]!;
    const direction: Direction = armIndex === 0 ? W : E;
    const cursorX = leadRect.x + (armIndex === 0 ? 0 : leadWidth);
    const cursorY = centerY;
    let openValue: Pip;
    if (leadIsDouble) {
      openValue = chain.lead[0];
    } else {
      openValue = (armIndex === 0 ? chain.lead[0] : chain.lead[1]) as Pip;
    }
    const armTurns = turnsByArm[armIndex] ?? [];
    const walk = walkArm(arm.tiles, 1, { x: cursorX, y: cursorY }, direction, openValue, chain.spinner, armIndex, armTurns, mainInitialPerpHalf, mainInitialAlongHalf, leadIsDouble);
    placed.push(...walk.placed);
    if (walk.spinnerCenter && !spinnerCenter) {
      spinnerCenter = walk.spinnerCenter;
      // Use the direction the arm was carrying when the double landed, not
      // the arm's initial direction — they differ whenever a fold preceded
      // the spinner, and the perpendicular arms (2, 3) must extend along
      // the spinner's long axis.
      spinnerMainDirection = walk.spinnerDirection ?? direction;
    }
    armEnds.push({
      armIndex,
      cx: walk.cursor.x,
      cy: walk.cursor.y,
      direction: walk.endDirection,
      // arm.tiles[0] is the lead (shared with the other main arm); a length
      // of 1 means no plays have been made on this arm yet — so folds have
      // no anchor tile and should be suppressed downstream.
      empty: arm.tiles.length <= 1,
      lastPerpHalf: walk.lastPerpHalf,
      lastAlongHalf: walk.lastAlongHalf,
      anchorIsDouble: walk.anchorIsDouble,
    });
  }

  // Perpendicular arms (2, 3) — only emitted once the spinner is covered on
  // its main axis. While uncovered, plays on these arms are illegal per
  // `Chain.canPlayOnArm`, so we omit their endpoints (no drop slots) and
  // skip tile placement (the arms are empty in that state anyway).
  const perpUnlocked = isSpinnerCovered(chain);
  for (const armIndex of [2, 3]) {
    if (!perpUnlocked) continue;
    if (armIndex >= chain.arms.length) continue;
    if (!spinnerCenter || !spinnerMainDirection) continue;
    const arm = chain.arms[armIndex]!;
    // Spinner long axis = perpendicular to main direction. Arm 2 exits the
    // "left" side of main travel; arm 3 exits the "right" side. (For
    // canonical horizontal main: N=up=left, S=down=right.)
    const perpL: Direction = {
      dx: (spinnerMainDirection.dy || 0) as -1 | 0 | 1,
      dy: (-spinnerMainDirection.dx || 0) as -1 | 0 | 1,
    };
    const perpR: Direction = {
      dx: (-spinnerMainDirection.dy || 0) as -1 | 0 | 1,
      dy: (spinnerMainDirection.dx || 0) as -1 | 0 | 1,
    };
    const direction: Direction = armIndex === 2 ? perpL : perpR;
    // Cursor: long-side edge of spinner (half of TILE_LONG out from spinner center).
    const cursorX = spinnerCenter.x + direction.dx * (TILE_LONG / 2);
    const cursorY = spinnerCenter.y + direction.dy * (TILE_LONG / 2);
    const spinnerTile = chain.spinner!;
    const openValue: Pip = spinnerTile[0];
    const armTurns = turnsByArm[armIndex] ?? [];
    // Perp arm: extends from the spinner along its long axis. Spinner's
    // perpendicular extent relative to that direction = TILE_SHORT,
    // along-direction extent = TILE_LONG (spinner long axis aligned with
    // the perp arm). The anchor for an empty perp arm is the spinner
    // itself (always a double); once a tile is played the walk updates
    // anchorIsDouble appropriately.
    const walk = walkArm(arm.tiles, 0, { x: cursorX, y: cursorY }, direction, openValue, null, armIndex, armTurns, TILE_SHORT / 2, TILE_LONG / 2, true);
    placed.push(...walk.placed);
    armEnds.push({
      armIndex,
      cx: walk.cursor.x,
      cy: walk.cursor.y,
      direction: walk.endDirection,
      empty: arm.tiles.length === 0,
      lastPerpHalf: walk.lastPerpHalf,
      lastAlongHalf: walk.lastAlongHalf,
      anchorIsDouble: walk.anchorIsDouble,
    });
  }

  return { tiles: placed, armEnds, bounds: boundsOf(placed) };
}

interface WalkResult {
  readonly placed: readonly PlacedTile[];
  readonly cursor: { x: number; y: number };
  readonly spinnerCenter: { x: number; y: number } | null;
  /**
   * Direction the arm was travelling at the moment the spinner was placed.
   * Used to orient the perpendicular arms (2, 3): they extend along the
   * spinner's long axis, which is perpendicular to whatever direction the
   * main arm was carrying when the double landed — that direction differs
   * from the arm's initial direction whenever a fold preceded the spinner.
   */
  readonly spinnerDirection: Direction | null;
  /** Direction of the open end after any auto-folds. */
  readonly endDirection: Direction;
  /** See ArmEndpoint.lastPerpHalf. */
  readonly lastPerpHalf: number;
  /** See ArmEndpoint.lastAlongHalf. */
  readonly lastAlongHalf: number;
  /** See ArmEndpoint.anchorIsDouble. */
  readonly anchorIsDouble: boolean;
}

/** 90° clockwise rotation: (dx, dy) → (-dy, dx). */
function rotateClockwise(d: Direction): Direction {
  return { dx: -d.dy as -1 | 0 | 1, dy: d.dx as -1 | 0 | 1 };
}

/** 90° counterclockwise rotation: (dx, dy) → (dy, -dx). */
function rotateCounterclockwise(d: Direction): Direction {
  return { dx: d.dy as -1 | 0 | 1, dy: -d.dx as -1 | 0 | 1 };
}

/** Resolve an explicit `Turn` to its rotated direction. `'auto'` returns `null`
 *  so the caller knows to apply the auto-fold heuristic instead. */
export function applyTurn(d: Direction, turn: Turn): Direction | null {
  switch (turn) {
    case 'straight':
      return d;
    case 'left':
      return rotateCounterclockwise(d);
    case 'right':
      return rotateClockwise(d);
    case 'auto':
      return null;
  }
}

function walkArm(
  tiles: readonly Tile[],
  startIndex: number,
  startCursor: { x: number; y: number },
  startDirection: Direction,
  startOpenValue: Pip,
  spinnerTile: Tile | null,
  armIndex: number,
  turns: readonly Turn[],
  initialPerpHalf: number,
  initialAlongHalf: number,
  initialAnchorIsDouble: boolean,
): WalkResult {
  const placed: PlacedTile[] = [];
  let cursor = { ...startCursor };
  let direction = startDirection;
  // The match value at the current open end — face for a double, outer pip
  // otherwise. Parallels `Chain.matchValueFor`; do NOT carry the scoring
  // sum here (e.g. 6 for [3|3]) or the next non-double will orient against
  // a pip value it can't actually contain.
  let openValue = startOpenValue;
  let spinnerCenter: { x: number; y: number } | null = null;
  let spinnerDirection: Direction | null = null;
  // Length accumulated in `direction` since the last fold (or since the arm
  // started). Used by the 'auto' (AI default) fold heuristic.
  let segmentDistance = 0;
  // Half the perpendicular extent (relative to current `direction`) of the
  // tile most recently placed — or the upstream anchor's extent before the
  // first placement. A fold shifts the cursor by this much along the NEW
  // direction so the folded tile reaches past the previous tile's perp edge.
  let lastPerpHalf = initialPerpHalf;
  // Half the along-direction extent of the same tile. A fold also backs the
  // cursor up along `-direction` by `lastAlongHalf - newTilePerpHalf` so the
  // folded non-double tile centers on the parent's NEAR half (its right/
  // bottom edge meeting the parent's midline) instead of straddling the
  // parent's outside corner.
  let lastAlongHalf = initialAlongHalf;
  // Tracks whether the current outermost tile (the anchor for the next play)
  // is a double, so callers can suppress fold options off it.
  let anchorIsDouble = initialAnchorIsDouble;

  for (let i = startIndex; i < tiles.length; i += 1) {
    const tile = tiles[i]!;
    const thisIsDouble = isDouble(tile);
    const footprint = thisIsDouble ? TILE_SHORT : TILE_LONG;
    const isSpinnerTile =
      spinnerTile !== null && spinnerCenter === null && tileEquals(tile, spinnerTile);

    // Resolve the per-tile turn. Explicit turns (player choice) override
    // the auto-fold heuristic; 'auto' falls back to the segment-length rule.
    // Doubles are exempt: they must always render perpendicular (lateral),
    // so we never fold the chain ON a double. If the auto-fold heuristic
    // would have fired here, the next non-double tile picks it up instead.
    const turn = turns[i] ?? 'auto';
    const explicit = applyTurn(direction, turn);
    let foldDir: Direction | null = null;
    if (!thisIsDouble) {
      if (explicit !== null && explicit !== direction) {
        foldDir = explicit;
      } else if (
        explicit === null &&
        segmentDistance > 0 &&
        segmentDistance + footprint > MAX_SEGMENT_LENGTH
      ) {
        foldDir = rotateClockwise(direction);
      }
    }

    if (foldDir !== null) {
      const oldDir = direction;
      direction = foldDir;
      // Push past parent's perp edge into the new direction, then back the
      // cursor up along -oldDir by `lastAlongHalf - SHORT/2` so the (non-
      // double) folded tile centers on the parent's near half. For a non-
      // double parent in-line this is LONG/2 - SHORT/2 = SHORT/2; for a
      // lateral double parent it is 0 (already on the parent's centerline
      // along oldDir).
      const backShift = lastAlongHalf - TILE_SHORT / 2;
      cursor = {
        x: cursor.x + direction.dx * lastPerpHalf - oldDir.dx * backShift,
        y: cursor.y + direction.dy * lastPerpHalf - oldDir.dy * backShift,
      };
      segmentDistance = 0;
    }

    if (thisIsDouble) {
      // Lateral placement: short edge along direction, long edge perpendicular.
      const orientation: TileRect['orientation'] = direction.dx !== 0 ? 'vertical' : 'horizontal';
      const w = orientation === 'horizontal' ? TILE_LONG : TILE_SHORT;
      const h = orientation === 'horizontal' ? TILE_SHORT : TILE_LONG;
      const center = {
        x: cursor.x + direction.dx * (TILE_SHORT / 2),
        y: cursor.y + direction.dy * (TILE_SHORT / 2),
      };
      const rect: TileRect = {
        x: center.x - w / 2,
        y: center.y - h / 2,
        long: TILE_LONG,
        short: TILE_SHORT,
        orientation,
      };
      placed.push({ rect, halfA: tile[0], halfB: tile[1], tile, armIndex, direction });
      if (isSpinnerTile) {
        spinnerCenter = center;
        spinnerDirection = direction;
      }
      cursor = {
        x: cursor.x + direction.dx * TILE_SHORT,
        y: cursor.y + direction.dy * TILE_SHORT,
      };
      // Double laid laterally: long axis perpendicular to `direction`.
      lastPerpHalf = TILE_LONG / 2;
      lastAlongHalf = TILE_SHORT / 2;
      anchorIsDouble = true;
      openValue = tile[0];
    } else {
      const orientation: TileRect['orientation'] = direction.dx !== 0 ? 'horizontal' : 'vertical';
      const w = orientation === 'horizontal' ? TILE_LONG : TILE_SHORT;
      const h = orientation === 'horizontal' ? TILE_SHORT : TILE_LONG;
      const center = {
        x: cursor.x + direction.dx * (TILE_LONG / 2),
        y: cursor.y + direction.dy * (TILE_LONG / 2),
      };
      const rect: TileRect = {
        x: center.x - w / 2,
        y: center.y - h / 2,
        long: TILE_LONG,
        short: TILE_SHORT,
        orientation,
      };
      const innerPip: Pip = openValue;
      const outerPip: Pip = (tile[0] === openValue ? tile[1] : tile[0]) as Pip;
      let halfA: Pip;
      let halfB: Pip;
      if (orientation === 'horizontal') {
        if (direction.dx > 0) {
          // east: inner (left) = innerPip, outer (right) = outerPip
          halfA = innerPip;
          halfB = outerPip;
        } else {
          // west: inner (right) = innerPip, outer (left) = outerPip
          halfA = outerPip;
          halfB = innerPip;
        }
      } else {
        if (direction.dy > 0) {
          // south: inner (top) = innerPip
          halfA = innerPip;
          halfB = outerPip;
        } else {
          // north: inner (bottom) = innerPip
          halfA = outerPip;
          halfB = innerPip;
        }
      }
      placed.push({ rect, halfA, halfB, tile, armIndex, direction });
      cursor = {
        x: cursor.x + direction.dx * TILE_LONG,
        y: cursor.y + direction.dy * TILE_LONG,
      };
      // Non-double laid in-line: short axis perpendicular to `direction`.
      lastPerpHalf = TILE_SHORT / 2;
      lastAlongHalf = TILE_LONG / 2;
      anchorIsDouble = false;
      openValue = outerPip;
    }
    segmentDistance += footprint;
  }

  return {
    placed,
    cursor,
    spinnerCenter,
    spinnerDirection,
    endDirection: direction,
    lastPerpHalf,
    lastAlongHalf,
    anchorIsDouble,
  };
}

/**
 * The tile rect a play would occupy if dropped on `endpoint` with the given
 * `turn`. `'straight'` continues in the arm's current direction; `'left'`/
 * `'right'` rotate 90° first; `'auto'` is treated as straight for slot-
 * preview purposes (the auto-fold heuristic only kicks in at layout time,
 * not at preview time). The player-side dispatcher computes one slot per
 * turn choice and offers them as snap targets.
 */
export function nextTileSlotForTurn(
  endpoint: ArmEndpoint,
  turn: Exclude<Turn, 'auto'>,
  isDoubleTile: boolean,
): TileRect {
  // Doubles always render perpendicular (lateral) — they never fold the
  // chain, regardless of `turn`. Mirrors the walkArm rule.
  if (isDoubleTile) return nextTileSlot(endpoint, true);
  if (turn === 'straight') return nextTileSlot(endpoint, isDoubleTile);
  // Non-double fold: anchor the new tile to the parent's near half by
  // (a) shifting the cursor along the new direction by the parent's perp
  // half-extent (so the new tile reaches past the parent's perp edge), and
  // (b) backing up along -oldDir by `lastAlongHalf - SHORT/2` so the new
  // tile's near edge meets the parent's perp midline. Mirrors the fold
  // logic in `walkArm`; without (b), the rect straddles the parent's
  // outside corner instead of attaching to its near half.
  const oldDir = endpoint.direction;
  const newDir = applyTurn(oldDir, turn) ?? oldDir;
  const backShift = endpoint.lastAlongHalf - TILE_SHORT / 2;
  const shifted: ArmEndpoint = {
    ...endpoint,
    cx: endpoint.cx + newDir.dx * endpoint.lastPerpHalf - oldDir.dx * backShift,
    cy: endpoint.cy + newDir.dy * endpoint.lastPerpHalf - oldDir.dy * backShift,
    direction: newDir,
  };
  return nextTileSlot(shifted, isDoubleTile);
}

/** True if two axis-aligned tile rects overlap (touching edges don't count). */
export function rectsOverlap(a: TileRect, b: TileRect): boolean {
  const ab = rectBounds(a);
  const bb = rectBounds(b);
  return !(
    ab.x + ab.width <= bb.x ||
    ab.x >= bb.x + bb.width ||
    ab.y + ab.height <= bb.y ||
    ab.y >= bb.y + bb.height
  );
}

/**
 * The tile rect a play would occupy if dropped on `endpoint`. Mirrors the
 * placement logic inside `walkArm`: non-doubles take a full long-axis step
 * in the arm direction, doubles take a short-axis step laterally (long axis
 * perpendicular to travel). Used by the drop-target overlay to draw a
 * tile-shaped outline where the dragged piece will land.
 */
export function nextTileSlot(endpoint: ArmEndpoint, isDoubleTile: boolean): TileRect {
  const dir = endpoint.direction;
  if (isDoubleTile) {
    const orientation: TileRect['orientation'] = dir.dx !== 0 ? 'vertical' : 'horizontal';
    const w = orientation === 'horizontal' ? TILE_LONG : TILE_SHORT;
    const h = orientation === 'horizontal' ? TILE_SHORT : TILE_LONG;
    const cx = endpoint.cx + dir.dx * (TILE_SHORT / 2);
    const cy = endpoint.cy + dir.dy * (TILE_SHORT / 2);
    return { x: cx - w / 2, y: cy - h / 2, long: TILE_LONG, short: TILE_SHORT, orientation };
  }
  const orientation: TileRect['orientation'] = dir.dx !== 0 ? 'horizontal' : 'vertical';
  const w = orientation === 'horizontal' ? TILE_LONG : TILE_SHORT;
  const h = orientation === 'horizontal' ? TILE_SHORT : TILE_LONG;
  const cx = endpoint.cx + dir.dx * (TILE_LONG / 2);
  const cy = endpoint.cy + dir.dy * (TILE_LONG / 2);
  return { x: cx - w / 2, y: cy - h / 2, long: TILE_LONG, short: TILE_SHORT, orientation };
}

/**
 * The tile rect a LEAD play would occupy at `(cx, cy)` — horizontal for a
 * non-double lead, vertical (lateral) for a double lead. Mirrors the lead
 * orientation logic in `layoutChain`.
 */
export function leadTileSlot(cx: number, cy: number, isDoubleTile: boolean): TileRect {
  const orientation: TileRect['orientation'] = isDoubleTile ? 'vertical' : 'horizontal';
  const w = orientation === 'horizontal' ? TILE_LONG : TILE_SHORT;
  const h = orientation === 'horizontal' ? TILE_SHORT : TILE_LONG;
  return { x: cx - w / 2, y: cy - h / 2, long: TILE_LONG, short: TILE_SHORT, orientation };
}

function boundsOf(tiles: readonly PlacedTile[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  if (tiles.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const t of tiles) {
    const w = t.rect.orientation === 'horizontal' ? t.rect.long : t.rect.short;
    const h = t.rect.orientation === 'horizontal' ? t.rect.short : t.rect.long;
    minX = Math.min(minX, t.rect.x);
    minY = Math.min(minY, t.rect.y);
    maxX = Math.max(maxX, t.rect.x + w);
    maxY = Math.max(maxY, t.rect.y + h);
  }
  return { minX, minY, maxX, maxY };
}

export const DIRECTIONS = { E, W, N, S } as const;
